import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";
import styles from "../SustainQueueScreen.style";
import mobileStyles from "../SustainQueueScreen.mobile.style";
import { QueueViewMode } from "../hooks/useSustainQueueListing";

type TeamOption = { id: number; name: string };

type Props = {
  subtitle: string;
  viewMode: QueueViewMode;
  onBackToPortal: () => void;
  onChangeView: (mode: QueueViewMode) => void;
  mobile?: boolean;
  totalCount?: number;
  filterCount?: number;
  onFiltersPress?: () => void;
  /** Team View: active Sustain Teams the user can switch the queue to */
  teams?: TeamOption[];
  /** Team currently being viewed (null until resolved) */
  viewingTeamId?: number | null;
  onSelectTeam?: (teamId: number) => void;
  /** false when viewing another team's queue (read-only) */
  isViewingOwnTeam?: boolean;
  isDark?: boolean;
  colors?: any;
};

const SustainQueueHeader: React.FC<Props> = ({
  subtitle,
  viewMode,
  onBackToPortal,
  onChangeView,
  mobile,
  totalCount,
  filterCount,
  onFiltersPress,
  teams,
  viewingTeamId,
  onSelectTeam,
  isViewingOwnTeam,
  isDark: isDarkProp,
  colors: colorsProp,
}) => {
  const { colors: themeColors, resolvedTheme } = useTheme();
  const isDark = isDarkProp !== undefined ? isDarkProp : resolvedTheme === "dark";
  const colors = colorsProp ?? themeColors;
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);
  const [teamMenuPos, setTeamMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const currentTeamName = teams?.find((t) => t.id === viewingTeamId)?.name;
  const teamTriggerId = "sq-team-switcher-trigger";
  const teamMenuId = "sq-team-switcher-menu";

  const repositionTeamMenu = useCallback(() => {
    if (Platform.OS !== "web") return;
    const el = document.getElementById(teamTriggerId);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTeamMenuPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(200, rect.width) });
    }
  }, []);

  const toggleTeamMenu = () => {
    repositionTeamMenu();
    setTeamMenuOpen((v) => !v);
  };

  // Close on outside click / reposition on scroll or resize — same escape-hatch pattern
  // used by the filter-bar dropdowns so this menu isn't clipped/painted under the
  // read-only banner (or anything else) rendered after the header in the DOM.
  useEffect(() => {
    if (!teamMenuOpen || Platform.OS !== "web") return;
    const handleClick = (e: MouseEvent) => {
      const trigger = document.getElementById(teamTriggerId);
      const menu = document.getElementById(teamMenuId);
      const target = e.target as Node;
      if ((!trigger || !trigger.contains(target)) && (!menu || !menu.contains(target))) {
        setTeamMenuOpen(false);
      }
    };
    const handleRepos = () => repositionTeamMenu();
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("resize", handleRepos);
    window.addEventListener("scroll", handleRepos, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("resize", handleRepos);
      window.removeEventListener("scroll", handleRepos, true);
    };
  }, [teamMenuOpen, repositionTeamMenu]);

  /* ── iPhone mobile header ──────────────────────────────── */
  if (mobile) {
    const mobileSubtitle =
      totalCount !== undefined ? `${totalCount} active tickets` : subtitle.split("·")[0].trim();
    return (
      <View style={[mobileStyles.headerBlock, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.divider }]}>
        <View style={mobileStyles.headerLeft}>
          <Pressable style={mobileStyles.backArrowWrap} onPress={onBackToPortal}>
            <MaterialIcons name="arrow-back" size={24} color={isDark ? colors.text.primary : "#1a2332"} />
          </Pressable>
          <View style={mobileStyles.headerTitleBlock}>
            <Text style={[mobileStyles.headerTitle, isDark && { color: colors.text.primary }]}>Sustain Queue</Text>
            <Text style={[mobileStyles.headerSubtitle, isDark && { color: colors.text.secondary }]}>{mobileSubtitle}</Text>
          </View>
        </View>
        <Pressable style={mobileStyles.filterIconBtn} onPress={onFiltersPress}>
          <MaterialIcons name="filter-list" size={26} color={isDark ? colors.text.primary : "#1a2332"} />
          {filterCount != null && filterCount > 0 ? (
            <View style={mobileStyles.filterBadge}>
              <Text style={mobileStyles.filterBadgeText}>{filterCount}</Text>
            </View>
          ) : null}
        </Pressable>
      </View>
    );
  }

  /* ── Web / iPad header ─────────────────────────────────── */
  const teamMenuItems = teams?.map((team) => (
    <Pressable
      key={team.id}
      style={[
        teamSwitcherStyles.menuItem,
        team.id === viewingTeamId && teamSwitcherStyles.menuItemActive,
      ]}
      onPress={() => {
        onSelectTeam?.(team.id);
        setTeamMenuOpen(false);
      }}
    >
      <Text
        style={[
          teamSwitcherStyles.menuItemText,
          team.id === viewingTeamId && teamSwitcherStyles.menuItemTextActive,
        ]}
      >
        {team.name}
      </Text>
      {team.id === viewingTeamId && (
        <MaterialIcons name="check" size={16} color="#005CBC" />
      )}
    </Pressable>
  ));

  return (
    <View style={styles.pageHeader}>
      <View style={styles.pageTitleBlock}>
        <View style={styles.breadcrumbRow}>
          <Pressable onPress={onBackToPortal}>
            <Text style={[styles.breadcrumbLink, isDark && { color: "#B0B0B0" }]}>Ticketing Portal</Text>
          </Pressable>
          <Text style={[styles.breadcrumbSeparator, isDark && { color: "#6E6E6E" }]}>/</Text>
          <Text style={[styles.breadcrumbCurrent, isDark && { color: "#F2F2F2" }]}>Sustain Queue</Text>
        </View>
        <Text style={[styles.pageTitle, isDark && { color: "#F2F2F2" }]}>Sustain Queue</Text>
        <Text style={[styles.pageSubtitle, isDark && { color: "#B0B0B0" }]}>{subtitle}</Text>
      </View>

      <View style={styles.pageActions}>
        {teams && teams.length > 0 && (
          <View style={{ position: "relative" }}>
            <Pressable
              // @ts-ignore web id
              id={teamTriggerId}
              nativeID={teamTriggerId}
              style={teamSwitcherStyles.trigger}
              onPress={toggleTeamMenu}
            >
              <View style={teamSwitcherStyles.dot} />
              <Text style={teamSwitcherStyles.triggerText} numberOfLines={1}>
                {currentTeamName ?? "Select team"}
              </Text>
              <MaterialIcons
                name={teamMenuOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                size={16}
                color="#4b4b4b"
              />
            </Pressable>
            {teamMenuOpen && Platform.OS !== "web" && (
              <View style={teamSwitcherStyles.menu}>{teamMenuItems}</View>
            )}
            {teamMenuOpen && Platform.OS === "web" && createPortal(
              <div
                id={teamMenuId}
                style={{
                  position: "fixed",
                  top: teamMenuPos.top,
                  left: teamMenuPos.left,
                  minWidth: teamMenuPos.width,
                  zIndex: 9999,
                }}
              >
                <View style={teamSwitcherStyles.menu}>{teamMenuItems}</View>
              </div>,
              document.body
            )}
          </View>
        )}
        {isViewingOwnTeam === false && (
          <View style={teamSwitcherStyles.readOnlyBadge}>
            <MaterialIcons name="visibility" size={13} color="#8B6508" />
            <Text style={teamSwitcherStyles.readOnlyBadgeText}>Read-only</Text>
          </View>
        )}
        <View style={[styles.switcher, isDark && { backgroundColor: "#202020", borderColor: "#404040" }]}>
          <Pressable
            style={({ pressed }) => [
              styles.switcherBtn,
              viewMode === "kanban" ? styles.switcherBtnActive : null,
              isDark && viewMode !== "kanban" ? { backgroundColor: "transparent" } : null,
              pressed ? styles.switcherBtnPressed : null,
            ]}
            onPress={() => onChangeView("kanban")}
          >
            <MaterialIcons name="view-kanban" size={14} color={viewMode === "kanban" ? "#ffffff" : (isDark ? "#B0B0B0" : "#4b4b4b")} />
            <Text style={[styles.switcherBtnText, viewMode === "kanban" ? styles.switcherBtnTextActive : null, isDark && viewMode !== "kanban" && { color: "#B0B0B0" }]}>Kanban</Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.switcherBtn,
              viewMode === "list" ? styles.switcherBtnActive : null,
              isDark && viewMode !== "list" ? { backgroundColor: "transparent" } : null,
              pressed ? styles.switcherBtnPressed : null,
            ]}
            onPress={() => onChangeView("list")}
          >
            <MaterialIcons name="table-rows" size={14} color={viewMode === "list" ? "#ffffff" : (isDark ? "#B0B0B0" : "#4b4b4b")} />
            <Text style={[styles.switcherBtnText, viewMode === "list" ? styles.switcherBtnTextActive : null, isDark && viewMode !== "list" && { color: "#B0B0B0" }]}>Table</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

const teamSwitcherStyles = StyleSheet.create({
  trigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#ffffff",
    maxWidth: 220,
  },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: "#005CBC" },
  triggerText: { fontSize: 13, fontWeight: "700", color: "#1a2332" },
  menu: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    minWidth: 220,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    paddingVertical: 4,
    zIndex: 200,
    ...(Platform.OS === "web" ? { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" } as any : { elevation: 6 }),
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  menuItemActive: { backgroundColor: "#eff6ff" },
  menuItemText: { fontSize: 13, color: "#1f2937" },
  menuItemTextActive: { color: "#005CBC", fontWeight: "700" },
  readOnlyBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#FFF6CC",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  readOnlyBadgeText: { fontSize: 11, fontWeight: "700", color: "#8B6508" },
});

export default SustainQueueHeader;
